import express from "express";
import contentDisposition from "content-disposition";
import {
    assertActionMethodBodyContract,
    assertActionPath,
    assertFlatScalarQuery,
    createDTISignPayload,
    DTI_DEFAULT_SIGN_HEADER_NAMES,
    DTIError,
    parseDTISignTimestamp,
    resolveActionMethod,
    resolveDTISignHeaderNames,
    verifyDTIHmacSha256,
} from "@napp/dti-core";
import type { ActionParam, ActionResult, DTIAction, DTISignHeaderNames, INonceStore, TContentType, TMethod } from "@napp/dti-core";
import type { NextFunction, Request, RequestHandler, Response, Router } from "express";
import type { z } from "zod";

type TActionSchema = z.ZodType;
type TAnyAction = DTIAction<any, any, any, any, any>;
type InferActionSchema<TSchema> = TSchema extends TActionSchema ? z.infer<TSchema> : void;
type DTIServerAuth<AuthContext> = [AuthContext] extends [undefined]
    ? { auth?: undefined }
    : { auth: AuthContext };
type DTIServerMeta<MetaContext> = [MetaContext] extends [undefined]
    ? { meta?: undefined }
    : { meta: MetaContext };

export type DTIAuthParam<TAction extends TAnyAction = TAnyAction> = {
    action: TAction;
    req: Request;
    res: Response;
};

export type DTIAuthVerify<AuthContext> = (
    param: DTIAuthParam,
) => AuthContext | Promise<AuthContext>;

export type DTIMetaParam<AuthContext> = {
    req: Request;
    res: Response;
} & DTIServerAuth<AuthContext>;

export type DTIMetaResolve<AuthContext, MetaContext> = (
    param: DTIMetaParam<AuthContext>,
) => MetaContext | Promise<MetaContext>;

export type DTIMetaOption<AuthContext, MetaContext> =
    | MetaContext
    | DTIMetaResolve<AuthContext, MetaContext>;

export type DTISignSecretParam = {
    keyId: string;
    req: Request;
    res: Response;
};

export type DTISignSecretProvider = (
    param: DTISignSecretParam,
) => string | Promise<string>;

export type DTIServerSignOptions = {
    getSecret: DTISignSecretProvider;
    nonceStore: INonceStore;
    toleranceMs?: number;
    headerNames?: Partial<DTISignHeaderNames>;
};

export type DTIErrorParseParam<TAction extends TAnyAction = TAnyAction> = {
    error: unknown;
    action: TAction;
    req: Request;
    res: Response;
};

export type DTIErrorParser = (
    param: DTIErrorParseParam,
) => DTIError | undefined | Promise<DTIError | undefined>;

export type DTIServerErrorOptions = {
    parse?: DTIErrorParser;
};

export type DTIExpressRouterOptions<AuthContext = undefined, MetaContext = undefined> = {
    auth?: DTIAuthVerify<AuthContext>;
    error?: DTIServerErrorOptions;
    meta?: DTIMetaOption<AuthContext, MetaContext>;
    sign?: DTIServerSignOptions;
};

export type DTIFileBody = Buffer | Uint8Array | ArrayBuffer | Blob | string;

export type DTITextResponse = {
    type: "text";
    body: string;
    contentType?: string;
    headers?: Record<string, string>;
    status?: number;
};

export type DTITextOptions = {
    contentType?: string;
    headers?: Record<string, string>;
    status?: number;
};

export type DTIFileResponse = {
    type: "file";
    body: DTIFileBody;
    filename?: string;
    contentType?: string;
    headers?: Record<string, string>;
    status?: number;
};

export type DTIFileOptions = {
    body: DTIFileBody;
    filename?: string;
    contentType?: string;
    headers?: Record<string, string>;
    status?: number;
};

export type DTIServerParam<TAction extends TAnyAction, AuthContext = undefined, MetaContext = undefined> =
    TAction extends DTIAction<infer _RSchema, infer QSchema, infer BSchema, infer _ResponseType, infer PSchema>
    ? ActionParam<InferActionSchema<QSchema>, InferActionSchema<BSchema>, InferActionSchema<PSchema>> & {
        req: Request;
        res: Response;
    } & DTIServerAuth<AuthContext>
    & DTIServerMeta<MetaContext>
    : never;

export type DTIServerResult<TAction extends TAnyAction> =
    TAction extends DTIAction<infer RSchema, infer _QSchema, infer _BSchema, infer ResponseType, infer _PSchema>
    ? ResponseType extends "text"
    ? string | DTITextResponse
    : ResponseType extends "file"
    ? DTIFileResponse
    : InferActionSchema<RSchema>
    : never;

export type DTIActionHandler<TAction extends TAnyAction, AuthContext = undefined, MetaContext = undefined> = (
    param: DTIServerParam<TAction, AuthContext, MetaContext>,
) => DTIServerResult<TAction> | Promise<DTIServerResult<TAction>>;

type TSuccessResponse = {
    success: true;
    data: unknown;
};

type TFailureResponse = {
    success: false;
    code: string;
    message: string;
    details?: Record<string, unknown>;
};

type TDTIResponse = TSuccessResponse | TFailureResponse;

export function dtiText(body: string, options?: DTITextOptions): DTITextResponse {
    return {
        type: "text",
        body,
        ...options,
    };
}

export function dtiFile(response: DTIFileOptions): DTIFileResponse {
    return {
        type: "file",
        ...response,
    };
}

export class DTIExpressRouter<AuthContext = undefined, MetaContext = undefined> {
    private readonly expressRouter = express.Router();
    private readonly actionNames = new Map<string, string>();
    private readonly routeKeys = new Map<string, string>();
    private readonly signHeaderNames: Readonly<DTISignHeaderNames>;
    private readonly signToleranceMs: number;

    constructor(private readonly options: DTIExpressRouterOptions<AuthContext, MetaContext> = {}) {
        this.signHeaderNames = options.sign
            ? resolveDTISignHeaderNames(options.sign.headerNames)
            : DTI_DEFAULT_SIGN_HEADER_NAMES;
        this.signToleranceMs = this.resolveSignTolerance(options.sign?.toleranceMs);
    }

    action<TAction extends TAnyAction>(action: TAction, handler: DTIActionHandler<TAction, AuthContext, MetaContext>): this {
        assertActionPath(action.path);
        assertActionMethodBodyContract(action.method, action.body);
        const path = action.path;
        const method = resolveActionMethod(action);
        const contentType = action.contentType || "json";
        const routeHandler = this.createRouteHandler(action, handler);

        this.assertUniqueAction(action, method, path);
        this.registerRoute(method, path, method === "GET"
            ? [routeHandler]
            : [this.createBodyParser(contentType), routeHandler]);

        return this;
    }

    router(): Router {
        return this.expressRouter;
    }

    private createRouteHandler<TAction extends TAnyAction>(
        action: TAction,
        handler: DTIActionHandler<TAction, AuthContext, MetaContext>,
    ): RequestHandler {
        return async (req, res, next) => {
            try {
                const param = await this.parseParam(action, req, res);

                if (action.validateParam && !action.validateParam(param)) {
                    throw new DTIError("Invalid action param", { code: "DTI_PARAM_VALIDATE_ERROR" });
                }

                const result = await handler(param);
                const responseType = action.responseType || "json";

                if (responseType === "text") {
                    const textResponse = this.parseTextResult(result);
                    if (action.validateResult && !this.validateResult(action.validateResult, textResponse.body)) {
                        throw new DTIError("Invalid action result", { code: "DTI_RESULT_VALIDATE_ERROR" });
                    }
                    this.sendTextResponse(res, textResponse);
                    return;
                }

                if (responseType === "file") {
                    const fileResponse = this.parseFileResult(result);
                    if (action.validateResult) {
                        const blob = await this.createFileBlob(fileResponse);
                        if (!this.validateResult(action.validateResult, blob)) {
                            throw new DTIError("Invalid action result", { code: "DTI_RESULT_VALIDATE_ERROR" });
                        }
                    }
                    await this.sendFileResponse(res, fileResponse);
                    return;
                }

                const validResult = this.parseJsonResult(action, result);

                if (action.validateResult && !this.validateResult(action.validateResult, validResult)) {
                    throw new DTIError("Invalid action result", { code: "DTI_RESULT_VALIDATE_ERROR" });
                }

                if (this.isBodylessStatus(res.statusCode)) {
                    res.end();
                    return;
                }

                res.json(this.createSuccessResponse(validResult));
            } catch (error) {
                await this.handleError(error, action, req, res, next);
            }
        };
    }

    private async parseParam<TAction extends TAnyAction>(
        action: TAction,
        req: Request,
        res: Response,
    ): Promise<DTIServerParam<TAction, AuthContext, MetaContext>> {
        const param = {
            req,
            res,
        } as DTIServerParam<TAction, AuthContext, MetaContext>;

        if (action.params) {
            const parsedPathParams = action.params.safeParse(req.params);
            if (!parsedPathParams.success) {
                throw new DTIError("Invalid action path params", { code: "DTI_PATH_PARAMS_VALIDATE_ERROR" });
            }
            param.params = parsedPathParams.data;
        }

        if (action.query) {
            assertFlatScalarQuery(req.query);
            const parsedQuery = action.query.safeParse(req.query);
            if (!parsedQuery.success) {
                throw new DTIError("Invalid action query", { code: "DTI_QUERY_VALIDATE_ERROR" });
            }
            assertFlatScalarQuery(parsedQuery.data);
            param.query = parsedQuery.data;
        }

        if (action.body) {
            const parsedBody = action.body.safeParse(req.body);
            if (!parsedBody.success) {
                throw new DTIError("Invalid action body", { code: "DTI_BODY_VALIDATE_ERROR" });
            }
            param.body = parsedBody.data;
        }

        await this.verifySignature(action, param, req, res);

        const auth = await this.verifyAuth(action, req, res);
        if (this.options.auth) {
            Object.assign(param, { auth });
        }

        const meta = await this.resolveMeta(req, res, auth);
        if (this.options.meta !== undefined) {
            Object.assign(param, { meta });
        }

        return param;
    }

    private async verifySignature<TAction extends TAnyAction>(
        action: TAction,
        param: DTIServerParam<TAction, AuthContext, MetaContext>,
        req: Request,
        res: Response,
    ): Promise<void> {
        const sign = this.options.sign;
        if (!sign) {
            return;
        }

        if (!action.signature) {
            throw new DTIError("Action signature is required", { code: "DTI_SIGNATURE_REQUIRED", status: 401 });
        }

        const keyId = this.getRequiredSignHeader(req, this.signHeaderNames.keyId);
        const timestamp = this.getRequiredSignHeader(req, this.signHeaderNames.timestamp);
        const nonce = this.getRequiredSignHeader(req, this.signHeaderNames.nonce);
        const signature = this.getRequiredSignHeader(req, this.signHeaderNames.signature);
        const toleranceMs = this.signToleranceMs;

        const timestampMs = this.validateSignTimestamp(timestamp, toleranceMs);

        const actionSignature = action.signature(param);
        const signPayload = createDTISignPayload({
            method: resolveActionMethod(action),
            pathWithQuery: req.url,
            timestamp,
            nonce,
            actionSignature,
        });
        const secret = await sign.getSecret({
            keyId,
            req,
            res,
        });

        if (!await verifyDTIHmacSha256(secret, signPayload, signature)) {
            throw new DTIError("Invalid request signature", { code: "DTI_SIGNATURE_INVALID", status: 401 });
        }

        const nonceKey = this.createNonceKey(keyId, nonce);
        const nonceTtlMs = Math.max(1, timestampMs + toleranceMs - Date.now());
        if (!await sign.nonceStore.consume(nonceKey, nonceTtlMs)) {
            throw new DTIError("Request nonce already used", { code: "DTI_REPLAY_DETECTED", status: 401 });
        }
    }

    private getRequiredSignHeader(req: Request, name: string): string {
        const value = req.header(name);
        if (!value) {
            throw new DTIError("Request signature is required", { code: "DTI_SIGNATURE_REQUIRED", status: 401 });
        }

        return value;
    }

    private validateSignTimestamp(timestamp: string, toleranceMs: number): number {
        const timestampMs = parseDTISignTimestamp(timestamp);
        if (timestampMs === undefined || Math.abs(Date.now() - timestampMs) > toleranceMs) {
            throw new DTIError("Request signature timestamp is invalid", { code: "DTI_SIGNATURE_TIMESTAMP_INVALID", status: 401 });
        }

        return timestampMs;
    }

    private resolveSignTolerance(toleranceMs: number | undefined): number {
        const resolvedToleranceMs = toleranceMs ?? 5 * 60 * 1000;
        if (!Number.isSafeInteger(resolvedToleranceMs) || resolvedToleranceMs <= 0) {
            throw new DTIError("Invalid DTI signing configuration", {
                code: "DTI_SIGN_CONFIG_ERROR",
                details: {
                    field: "toleranceMs",
                    reason: "toleranceMs must be a positive safe integer",
                },
            });
        }

        return resolvedToleranceMs;
    }

    private createNonceKey(keyId: string, nonce: string): string {
        return `${keyId}:${nonce}`;
    }

    private async verifyAuth<TAction extends TAnyAction>(
        action: TAction,
        req: Request,
        res: Response,
    ): Promise<AuthContext | undefined> {
        if (!this.options.auth) {
            return undefined;
        }

        return await this.options.auth({ action, req, res });
    }

    private async resolveMeta(req: Request, res: Response, auth: AuthContext | undefined): Promise<MetaContext | undefined> {
        const meta = this.options.meta;
        if (meta === undefined) {
            return undefined;
        }

        if (typeof meta === "function") {
            const metaParam = {
                req,
                res,
            } as DTIMetaParam<AuthContext>;

            if (this.options.auth) {
                Object.assign(metaParam, { auth });
            }

            return await (meta as DTIMetaResolve<AuthContext, MetaContext>)(metaParam);
        }

        return meta;
    }

    private parseJsonResult<TAction extends TAnyAction>(
        action: TAction,
        result: DTIServerResult<TAction>,
    ): DTIServerResult<TAction> {
        if (!action.result) {
            return undefined as DTIServerResult<TAction>;
        }

        const parsedResult = action.result.safeParse(result);
        if (!parsedResult.success) {
            throw new DTIError("Invalid action result", { code: "DTI_RESULT_PARSE_ERROR" });
        }

        return parsedResult.data as DTIServerResult<TAction>;
    }

    private parseTextResult(result: unknown): DTITextResponse {
        if (this.isTextResponse(result)) {
            return result;
        }

        if (typeof result !== "string") {
            throw new DTIError("Invalid text result", { code: "DTI_RESULT_PARSE_ERROR" });
        }

        return dtiText(result);
    }

    private parseFileResult(result: unknown): DTIFileResponse {
        if (!this.isFileResponse(result)) {
            throw new DTIError("Invalid file result", { code: "DTI_RESULT_PARSE_ERROR" });
        }

        return result;
    }

    private validateResult<TResult>(
        validate: (param: ActionResult<TResult>) => boolean,
        result: TResult,
    ): boolean {
        return validate({ result } as ActionResult<TResult>);
    }

    private registerRoute(method: TMethod, path: string, handlers: RequestHandler[]): void {
        switch (method) {
            case "GET":
                this.expressRouter.get(path, ...handlers);
                return;
            case "PUT":
                this.expressRouter.put(path, ...handlers);
                return;
            case "DELETE":
                this.expressRouter.delete(path, ...handlers);
                return;
            case "PATCH":
                this.expressRouter.patch(path, ...handlers);
                return;
            case "POST":
            default:
                this.expressRouter.post(path, ...handlers);
        }
    }

    private assertUniqueAction(action: TAnyAction, method: TMethod, path: string): void {
        const existingNameRoute = this.actionNames.get(action.name);
        if (existingNameRoute) {
            throw new DTIError("Duplicate DTI action name", {
                code: "DTI_ACTION_NAME_DUPLICATE",
                details: {
                    name: action.name,
                    existingRoute: existingNameRoute,
                    route: this.createRouteKey(method, path),
                },
            });
        }

        const routeKey = this.createRouteKey(method, path);
        const existingRouteAction = this.routeKeys.get(routeKey);
        if (existingRouteAction) {
            throw new DTIError("Duplicate DTI action route", {
                code: "DTI_ACTION_ROUTE_DUPLICATE",
                details: {
                    route: routeKey,
                    existingActionName: existingRouteAction,
                    actionName: action.name,
                },
            });
        }

        this.actionNames.set(action.name, routeKey);
        this.routeKeys.set(routeKey, action.name);
    }

    private createRouteKey(method: TMethod, path: string): string {
        return `${method} ${this.normalizeRoutePath(path)}`;
    }

    private normalizeRoutePath(path: string): string {
        if (path.length > 1 && path.endsWith("/")) {
            return path.slice(0, -1);
        }

        return path;
    }

    private createBodyParser(contentType: TContentType): RequestHandler {
        switch (contentType) {
            case "form":
                return express.urlencoded({ extended: true });
            case "text":
                return express.text();
            case "json":
            default:
                return express.json();
        }
    }

    private sendTextResponse(res: Response, response: DTITextResponse): void {
        this.setHeaders(res, response.headers);
        if (response.status !== undefined) {
            res.status(response.status);
        }
        res.setHeader("Content-Type", response.contentType || "text/plain; charset=utf-8");
        if (this.isBodylessStatus(res.statusCode)) {
            res.end();
            return;
        }
        res.end(response.body);
    }

    private async sendFileResponse(res: Response, response: DTIFileResponse): Promise<void> {
        const disposition = response.filename === undefined
            ? undefined
            : this.createContentDisposition(response.filename);
        this.setHeaders(res, response.headers);
        if (response.status !== undefined) {
            res.status(response.status);
        }

        const contentType = response.contentType || "application/octet-stream";
        res.setHeader("Content-Type", contentType);

        if (disposition !== undefined) {
            res.setHeader("Content-Disposition", disposition);
        }

        if (this.isBodylessStatus(res.statusCode)) {
            res.end();
            return;
        }

        res.send(await this.normalizeFileBody(response.body));
    }

    private setHeaders(res: Response, headers?: Record<string, string>): void {
        if (!headers) {
            return;
        }

        for (const [key, value] of Object.entries(headers)) {
            res.setHeader(key, value);
        }
    }

    private createSuccessResponse(data: unknown): TDTIResponse {
        return {
            success: true,
            data,
        };
    }

    private createFailureResponse(error: DTIError): TDTIResponse {
        const response: TFailureResponse = {
            success: false,
            code: error.code || "DTI_ERROR",
            message: error.message,
        };

        if (error.details) {
            response.details = error.details;
        }

        return response;
    }

    private async handleError<TAction extends TAnyAction>(
        error: unknown,
        action: TAction,
        req: Request,
        res: Response,
        next: NextFunction,
    ): Promise<void> {
        if (res.headersSent) {
            next(error);
            return;
        }

        const parsedError = await this.parseError(error, action, req, res);
        if (parsedError) {
            res.status(this.getErrorStatus(parsedError)).json(this.createFailureResponse(parsedError));
            return;
        }

        res.status(500).json(this.createFailureResponse(new DTIError("Internal server error", { code: "DTI_INTERNAL_ERROR" })));
    }

    private async parseError<TAction extends TAnyAction>(
        error: unknown,
        action: TAction,
        req: Request,
        res: Response,
    ): Promise<DTIError | undefined> {
        if (error instanceof DTIError) {
            return error;
        }

        const parser = this.options.error?.parse;
        if (!parser) {
            return undefined;
        }

        try {
            const parsedError = await parser({
                error,
                action,
                req,
                res,
            });

            return parsedError instanceof DTIError ? parsedError : undefined;
        } catch (parserError) {
            return parserError instanceof DTIError ? parserError : undefined;
        }
    }

    private getErrorStatus(error: DTIError): number {
        if (error.status) {
            return error.status;
        }

        switch (error.code) {
            case "AUTH_REQUIRED":
            case "INVALID_TOKEN":
            case "DTI_SIGNATURE_REQUIRED":
            case "DTI_SIGNATURE_TIMESTAMP_INVALID":
            case "DTI_SIGNATURE_INVALID":
            case "DTI_REPLAY_DETECTED":
                return 401;
            case "PERMISSION_DENIED":
                return 403;
            case "DTI_PATH_PARAMS_VALIDATE_ERROR":
            case "DTI_QUERY_VALIDATE_ERROR":
            case "DTI_BODY_VALIDATE_ERROR":
            case "DTI_PARAM_VALIDATE_ERROR":
                return 400;
            case "DTI_RESULT_PARSE_ERROR":
            case "DTI_RESULT_VALIDATE_ERROR":
                return 500;
            default:
                return 500;
        }
    }

    private isTextResponse(result: unknown): result is DTITextResponse {
        return !!result && typeof result === "object" && "type" in result && result.type === "text";
    }

    private isFileResponse(result: unknown): result is DTIFileResponse {
        return !!result && typeof result === "object" && "type" in result && result.type === "file" && "body" in result;
    }

    private async createFileBlob(response: DTIFileResponse): Promise<Blob> {
        if (this.isBlob(response.body)) {
            return response.body;
        }

        const body = await this.normalizeFileBody(response.body);

        return new Blob([new Uint8Array(body)], {
            type: response.contentType || "application/octet-stream",
        });
    }

    private async normalizeFileBody(body: DTIFileBody): Promise<Buffer> {
        if (typeof body === "string") {
            return Buffer.from(body);
        }

        if (this.isBlob(body)) {
            return Buffer.from(await body.arrayBuffer());
        }

        if (body instanceof ArrayBuffer) {
            return Buffer.from(body);
        }

        return Buffer.from(body.buffer, body.byteOffset, body.byteLength);
    }

    private isBlob(value: unknown): value is Blob {
        return typeof Blob !== "undefined" && value instanceof Blob;
    }

    private createContentDisposition(filename: string): string {
        if (typeof filename !== "string" || filename.length === 0) {
            throw new DTIError("Invalid file response filename", { code: "DTI_FILE_NAME_ERROR" });
        }

        const normalizedFilename = filename.normalize("NFC");
        if (normalizedFilename === "."
            || normalizedFilename === ".."
            || /[\\/]/.test(normalizedFilename)
            || /[\x00-\x1F\x7F]/.test(normalizedFilename)) {
            throw new DTIError("Invalid file response filename", { code: "DTI_FILE_NAME_ERROR" });
        }

        const asciiFallback = this.createAsciiFilenameFallback(normalizedFilename);
        return contentDisposition(normalizedFilename, {
            type: "attachment",
            fallback: asciiFallback,
        });
    }

    private createAsciiFilenameFallback(filename: string): string {
        const fallback = filename.replace(/[^A-Za-z0-9._ -]/g, "_");
        return fallback.trim().length > 0 ? fallback : "download";
    }

    private isBodylessStatus(status: number): boolean {
        return status === 204 || status === 205;
    }
}

export function createDTIExpressRouter<AuthContext = undefined, MetaContext = undefined>(
    options?: DTIExpressRouterOptions<AuthContext, MetaContext>,
): DTIExpressRouter<AuthContext, MetaContext> {
    return new DTIExpressRouter(options);
}
